import merge from 'lodash/merge';
import React, { ComponentType, Dispatch, useMemo, useState, useEffect, useCallback } from 'react';
import { JsonFormsDispatch, JsonFormsStateContext, withJsonFormsContext } from '@jsonforms/react';
import {
  composePaths,
  ControlElement,
  findUISchema,
  JsonFormsRendererRegistryEntry,
  JsonSchema,
  moveDown,
  moveUp,
  update,
  JsonFormsCellRendererRegistryEntry,
  JsonFormsUISchemaRegistryEntry,
  createId,
  removeId,
  ArrayTranslations,
  computeChildLabel,
  UpdateArrayContext,
  CoreActions,
  JsonSchema7,
} from '@jsonforms/core';
import { Accordion, Avatar, Tooltip, Grid, ActionIcon } from '@mantine/core';
import { IconArrowUp, IconArrowDown, IconTrash } from '@tabler/icons-react';
import FileFormatInArrayOneOf, { fileFormatInArrayOneOfTester } from '../complex/mantineOneOf/FileFormatInArrayOneOf';

const iconStyle: any = { float: 'right' };

interface OwnPropsOfExpandPanel {
  enabled: boolean;
  index: number;
  path: string;
  uischema: ControlElement;
  schema: JsonSchema;
  expanded: boolean;
  renderers?: JsonFormsRendererRegistryEntry[];
  cells?: JsonFormsCellRendererRegistryEntry[];
  uischemas?: JsonFormsUISchemaRegistryEntry[];
  rootSchema: JsonSchema;
  enableMoveUp: boolean;
  enableMoveDown: boolean;
  config: any;
  childLabelProp?: string;
  handleExpansion(panel: string): (event: any, expanded: boolean) => void;
  translations: ArrayTranslations;
  disableRemove?: boolean;
}

interface StatePropsOfExpandPanel extends OwnPropsOfExpandPanel {
  childLabel: string;
  childPath: string;
  enableMoveUp: boolean;
  enableMoveDown: boolean;
}

/**
 * Dispatch props of a table control
 */
export interface DispatchPropsOfExpandPanel {
  removeItems(path: string, toDelete: number[]): (event: any) => void;
  moveUp(path: string, toMove: number): (event: any) => void;
  moveDown(path: string, toMove: number): (event: any) => void;
}

export interface ExpandPanelProps extends StatePropsOfExpandPanel, DispatchPropsOfExpandPanel {}

const ExpandPanelRendererComponent = (props: ExpandPanelProps) => {
  const [labelHtmlId] = useState<string>(createId('expand-panel'));

  useEffect(() => {
    return () => {
      removeId(labelHtmlId);
    };
  }, [labelHtmlId]);

  const {
    enabled,
    childLabel,
    childPath,
    index,
    expanded,
    moveDown,
    moveUp,
    enableMoveDown,
    enableMoveUp,
    handleExpansion,
    removeItems,
    path,
    rootSchema,
    schema,
    uischema,
    uischemas,
    renderers,
    cells,
    config,
    translations,
    disableRemove,
  } = props;

  const foundUISchema = useMemo(
    () => findUISchema(uischemas!, schema, uischema.scope, path, undefined, uischema, rootSchema),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [uischemas, schema, uischema.scope, path, uischema, rootSchema]
  );

  const appliedUiSchemaOptions = merge({}, config, uischema.options);
  const showSortButtons = appliedUiSchemaOptions.showSortButtons || appliedUiSchemaOptions.showArrayLayoutSortButtons;

  return (
    <Accordion chevronPosition='left' defaultValue={expanded ? 'value' : null}>
      <Accordion.Item value={'value'}>
        <Accordion.Control onClick={() => handleExpansion(childPath)} component='span'>
          <Grid>
            <Grid.Col span={{ xs: 7, md: 9 }}>
              <Grid>
                <Grid.Col span={{ xs: 2, md: 1 }}>
                  <Avatar aria-label='Index'>{index + 1}</Avatar>
                </Grid.Col>
                <Grid.Col span={{ xs: 10, md: 11 }}>
                  <div style={{ width: 'fit-content' }} id={labelHtmlId}>
                    <RowContent
                      childLabel={childLabel}
                      childPath={childPath}
                      enabled={enabled}
                      schema={schema}
                      path={path}
                      renderers={renderers ?? []}
                    />
                  </div>
                </Grid.Col>
              </Grid>
            </Grid.Col>
            <Grid.Col span={{ xs: 5, md: 3 }}>
              <Grid justify='flex-end'>
                {showSortButtons && enabled && (
                  <>
                    <Grid.Col>
                      <Tooltip
                        label={translations.up}
                        position='bottom'
                        withArrow
                        opened={enableMoveUp ? undefined : false}
                      >
                        <ActionIcon
                          onClick={() => moveUp(path, index)}
                          style={iconStyle}
                          disabled={!enableMoveUp}
                          aria-label={translations.upAriaLabel}
                        >
                          <IconArrowUp />
                        </ActionIcon>
                      </Tooltip>
                    </Grid.Col>
                    <Grid.Col>
                      <Tooltip
                        label={translations.down}
                        position='bottom'
                        withArrow
                        opened={enableMoveDown ? undefined : false}
                      >
                        <ActionIcon
                          onClick={() => moveDown(path, index)}
                          style={iconStyle}
                          disabled={!enableMoveDown}
                          aria-label={translations.downAriaLabel}
                        >
                          <IconArrowDown />
                        </ActionIcon>
                      </Tooltip>
                    </Grid.Col>
                  </>
                )}
                {enabled && !disableRemove && (
                  <Grid.Col>
                    <Tooltip label={translations.removeTooltip} position='bottom' withArrow>
                      <ActionIcon
                        mr='sm'
                        onClick={removeItems(path, [index])}
                        style={iconStyle}
                        aria-label={translations.removeAriaLabel}
                        bg='red.4'
                      >
                        <IconTrash />
                      </ActionIcon>
                    </Tooltip>
                  </Grid.Col>
                )}
              </Grid>
            </Grid.Col>
          </Grid>
        </Accordion.Control>
        <Accordion.Panel>
          <JsonFormsDispatch
            enabled={enabled}
            schema={schema}
            uischema={foundUISchema}
            path={childPath}
            key={childPath}
            renderers={renderers}
            cells={cells}
          />
        </Accordion.Panel>
      </Accordion.Item>
    </Accordion>
  );
};

export const ExpandPanelRenderer = React.memo(ExpandPanelRendererComponent);

/**
 * Maps state to dispatch properties of an expand pandel control.
 *
 * @param dispatch the store's dispatch method
 * @returns {DispatchPropsOfArrayControl} dispatch props of an expand panel control
 */
export const CtxDispatchToExpandPanelProps: (dispatch: Dispatch<CoreActions>) => DispatchPropsOfExpandPanel = (
  dispatch
) => ({
  removeItems: useCallback(
    (path: string, toDelete: number[]) =>
      (event: any): void => {
        event.stopPropagation();
        dispatch(
          update(
            path,
            (array) => {
              toDelete
                .sort()
                .reverse()
                .forEach((s) => array.splice(s, 1));
              return array;
            },
            { type: 'REMOVE', indices: toDelete } as UpdateArrayContext
          )
        );
      },
    [dispatch]
  ),
  moveUp: useCallback(
    (path: string, toMove: number) =>
      (event: any): void => {
        event.stopPropagation();
        dispatch(
          update(
            path,
            (array) => {
              moveUp(array, toMove);
              return array;
            },
            {
              type: 'MOVE',
              moves: [{ from: toMove, to: toMove - 1 }],
            } as UpdateArrayContext
          )
        );
      },
    [dispatch]
  ),
  moveDown: useCallback(
    (path: string, toMove: number) =>
      (event: any): void => {
        event.stopPropagation();
        dispatch(
          update(
            path,
            (array) => {
              moveDown(array, toMove);
              return array;
            },
            {
              type: 'MOVE',
              moves: [{ from: toMove, to: toMove + 1 }],
            } as UpdateArrayContext
          )
        );
      },
    [dispatch]
  ),
});

/**
 * Map state to control props.
 * @param state the JSON Forms state
 * @param ownProps any own props
 * @returns {StatePropsOfControl} state props for a control
 */
export const WithContextToExpandPanelProps = (Component: ComponentType<ExpandPanelProps>) => {
  return function WithContextToExpandPanelProps({
    ctx,
    props,
  }: {
    ctx: JsonFormsStateContext;
    props: ExpandPanelProps;
  }) {
    const dispatchProps = CtxDispatchToExpandPanelProps(ctx.dispatch!);
    const {
      // eslint is unable to detect that these props are "checked" via Typescript already
      // eslint-disable-next-line react/prop-types
      childLabelProp,
      // eslint-disable-next-line react/prop-types
      schema,
      // eslint-disable-next-line react/prop-types
      uischema,
      // eslint-disable-next-line react/prop-types
      rootSchema,
      // eslint-disable-next-line react/prop-types
      path,
      // eslint-disable-next-line react/prop-types
      index,
      // eslint-disable-next-line react/prop-types
      uischemas,
    } = props;
    const childPath = composePaths(path, `${index}`);

    const childLabel = useMemo(() => {
      return computeChildLabel(
        ctx.core?.data,
        childPath,
        childLabelProp!,
        schema,
        rootSchema,
        ctx.i18n?.translate!,
        uischema
      );
    }, [ctx.core?.data, childPath, childLabelProp, schema, rootSchema, ctx.i18n?.translate, uischema]);

    return (
      <Component {...props} {...dispatchProps} childLabel={childLabel} childPath={childPath} uischemas={uischemas} />
    );
  };
};

export const withJsonFormsExpandPanelProps = (
  Component: ComponentType<ExpandPanelProps>
): ComponentType<OwnPropsOfExpandPanel> => withJsonFormsContext(WithContextToExpandPanelProps(Component));

export default withJsonFormsExpandPanelProps(ExpandPanelRenderer);

const RowContent = ({
  path,
  childPath,
  childLabel,
  enabled,
  schema,
  renderers,
}: {
  path: string;
  childLabel: string;
  childPath: string;
  enabled: boolean;
  schema: JsonSchema;
  renderers: JsonFormsRendererRegistryEntry[];
}) => {
  if (path === 'export' && schema.properties && schema.properties.format) {
    const customSchema = {
      type: 'object',
      properties: {
        format: schema.properties.format as JsonSchema7,
      },
    };

    const customUiSchema = {
      type: 'HorizontalLayout',
      elements: [
        {
          type: 'Control',
          scope: '#/properties/format',
        },
      ],
    };

    return (
      <div onClick={(e) => e.stopPropagation()}>
        <JsonFormsDispatch
          enabled={enabled}
          schema={customSchema}
          uischema={customUiSchema}
          path={childPath}
          renderers={[...renderers, { renderer: FileFormatInArrayOneOf, tester: fileFormatInArrayOneOfTester }]}
        />
      </div>
    );
  }

  return childLabel;
};
